import { groupBy, mapValues } from "lodash";
import { UtilityFeature } from "../../types";
import { useMemo } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Accordion } from "react-migration/components/Accordion";
import cn from "clsx";
import { SelectionType, SelectionFeature } from "src/js/stores/map/store";
import { useUtilitiesLayerTypeContext } from "../../UtilitiesContext";
import {
  logUtilitiesReportEvent,
  ViewType,
} from "react-migration/domains/sites/components/SiteCard/ReportSidebar/logUtilitiesEvent";

interface UtilitiesListProps {
  features: UtilityFeature[];
  setDetailSelection(selection: SelectionFeature | undefined): void;
}

export const UtilitiesList = ({ features, setDetailSelection }: UtilitiesListProps) => {
  const { expandedCategory, setExpandedCategory } = useUtilitiesLayerTypeContext();

  const { t } = useTranslation();
  const groupedFeatures = useMemo(() => {
    const featuresGroupedByCategory = groupBy(features, (feature: UtilityFeature) => feature.properties.Category);
    return mapValues(featuresGroupedByCategory, (features: UtilityFeature[]) => ({
      providers: groupBy(features, (feature: UtilityFeature) => feature.properties.Provider),
      colour: features[0].properties.fill ?? features[0].properties.stroke,
    }));
  }, [features]);

  const onCategoryExpanded = (category: string) => {
    setExpandedCategory(category);
    logUtilitiesReportEvent({
      name: "Utilities Report - Line Clicked/Drop Down Clicked",
      context: {
        viewType: ViewType.MAP,
        type: category,
      },
    });
  };

  return (
    <div data-testid="utilities-list">
      <Accordion
        value={expandedCategory}
        onValueChange={onCategoryExpanded}
        type="single"
        className="flex flex-col m-2 border divide-y rounded-sm border-border-action divide-border-base-divider"
        collapsible
      >
        {Object.entries(groupedFeatures).map(([categoryName, categoryData]) => (
          <Accordion.Item key={categoryName} value={categoryName}>
            <Accordion.Trigger asChild>
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-sm" style={{ backgroundColor: categoryData.colour }} />
                  <div className="font-semibold" data-testid="utility-list-category">
                    {categoryName}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="flex items-center h-6 gap-1 px-3 text-xs text-white bg-red-500 rounded-full">
                    <div className="font-bold">{Object.entries(categoryData.providers).length}</div>
                    <div>{t("utilities.affected")}</div>
                  </div>
                  <i
                    className={cn("text-2xl", {
                      "icon-lt-arrow-up-s-line": categoryName === expandedCategory,
                      "icon-lt-arrow-down-s-line": categoryName !== expandedCategory,
                    })}
                  />
                </div>
              </div>
            </Accordion.Trigger>
            <Accordion.Content className="bg-white">
              <UtilityProvidersList
                utilitiesGroupedByProvider={categoryData.providers}
                categoryColor={categoryData.colour}
                setDetailSelection={setDetailSelection}
              />
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

interface UtilityProvidersListProps {
  utilitiesGroupedByProvider: Record<string, UtilityFeature[]>;
  categoryColor: string;
  setDetailSelection(selection: SelectionFeature | undefined): void;
}

const UtilityProvidersList = ({
  utilitiesGroupedByProvider,
  categoryColor,
  setDetailSelection,
}: UtilityProvidersListProps) => {
  const { expandedProvider, setExpandedProvider } = useUtilitiesLayerTypeContext();

  return (
    <div data-testid="utilities-providers-list">
      <Accordion
        value={expandedProvider}
        onValueChange={setExpandedProvider}
        type="single"
        className="flex flex-col divide-y divide-border-base-divider"
        collapsible
      >
        {Object.entries(utilitiesGroupedByProvider).map(([providerName, utilityFeatures]) => (
          <Accordion.Item key={providerName} value={providerName}>
            <Accordion.Trigger asChild>
              <div className="flex items-center justify-between py-3 pl-5 pr-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-sm" style={{ backgroundColor: categoryColor }} />
                  <span className="font-semibold text-text-base-active" data-testid="utilities-provider-name">
                    {providerName} ({utilityFeatures.length})
                  </span>
                </div>
                <i
                  className={cn("text-2xl", {
                    "icon-lt-arrow-up-s-line": providerName === expandedProvider,
                    "icon-lt-arrow-down-s-line": providerName !== expandedProvider,
                  })}
                />
              </div>
            </Accordion.Trigger>
            <Accordion.Content className="bg-white">
              {utilityFeatures.map((utility) => (
                <UtilityListItem
                  key={utility.properties.UniqueID}
                  utility={utility}
                  setDetailSelection={setDetailSelection}
                />
              ))}
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

interface UtilityListItemProps {
  utility: UtilityFeature;
  setDetailSelection(selection: SelectionFeature | undefined): void;
}

function UtilityListItem({ utility: { properties }, setDetailSelection }: Readonly<UtilityListItemProps>) {
  const itemText = [properties.UniqueID, properties.Provider].join(" ");
  return (
    <button
      key={properties.UniqueID}
      className="flex items-center w-full gap-2 px-6 py-3 pr-3 text-text-base-active hover:text-text-base-active-hover"
      onClick={() =>
        setDetailSelection({
          type: SelectionType.UTILITY_REPORT,
          id: properties.UniqueID,
        })
      }
    >
      <span
        className="w-4 h-4 rounded-sm shrink-0"
        style={{
          backgroundColor: properties.fill ?? properties.stroke,
        }}
      />
      <span className="text-left truncate" title={itemText} data-testid="utilities-list-item-title">
        {itemText}
      </span>
    </button>
  );
}
